import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';

export type SectionLabelElement = 'div' | 'h2' | 'h3' | 'h4';

export type SectionLabelProps<T extends SectionLabelElement = 'div'> = Omit<
  ComponentPropsWithRef<T>,
  'style'
> & {
  as?: T;
  /*
   * The half-sentence after the em dash — "doelen, voortgang, kwaliteit". It
   * is part of the label, not a description under it, so it sits on the same
   * line in the same element and drops the caps treatment. A template's
   * section names are terse enough to be cryptic on their own; this is what
   * makes them readable without adding a second line of chrome.
   */
  tagline?: ReactNode;
};

/*
 * The small uppercase label that titles a block inside a card — a preparation
 * theme, a verslag section, a group of action points.
 *
 * Distinct from a card's own title (CardTitle): that names the card, this names
 * a division within it. Same typographic family, one level quieter.
 *
 * `as` because the right element depends on where it sits: a heading when it
 * titles real content that a screen-reader user should be able to jump to, a
 * plain div when the card around it is already the heading.
 */
export function SectionLabel<T extends SectionLabelElement = 'div'>({
  as,
  className,
  tagline,
  children,
  ...props
}: SectionLabelProps<T>) {
  /*
   * TS cannot type-check JSX against a union of tags (refs are invariant), so
   * the render site is cast to 'div'; SectionLabelProps<T> checks the call site.
   */
  const Component = (as ?? 'div') as 'div';
  return (
    <Component
      data-slot='section-label'
      className={cn(
        'text-xs font-bold tracking-widest text-ink-muted uppercase',
        className,
      )}
      {...(props as Omit<ComponentPropsWithRef<'div'>, 'style'>)}
    >
      {children}
      {tagline !== undefined && tagline !== null && (
        <span
          data-slot='section-label-tagline'
          className='font-normal tracking-normal normal-case'
        >
          {' — '}
          {tagline}
        </span>
      )}
    </Component>
  );
}
